/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 109 lines
 * - Adding: ~10 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    INVENTORY_PAGE_SPLIT_HOOK_UPLOAD
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Extracted upload logic from InventoryPage.tsx.
 */

import React, { useState } from "react";

import { parseFileToRows, chunkRows } from "../../../../utils/xlsxChunker";
import { createUploadJob, pushChunk, completeJob, getActiveJob, abortJob } from "../../../../services/inventoryUploads";
import { notify } from "../../../../utils/notifications";
import { MAX_ROWS_PER_JOB, MAX_ROWS_PER_CHUNK } from "../utils/inventoryHelpers";

export const useInventoryUpload = () => {

  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showUploadConfirm, setShowUploadConfirm] = useState(false);
  // [2026-09-15] UPLOAD_ERROR_VISIBILITY: keep the job id so the inventory page can show
  // live processing status and the failure summary in-app. Previously the user was
  // navigated away and the only feedback was a toast + email.
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  // [2026-09-16] chunk-send progress, so the panel can show movement while the browser is
  // still uploading and the server has not begun processing yet.
  const [sendProgress, setSendProgress] = useState<{ sent: number; total: number } | null>(null);

  // [2026-09-16] Reattach to an upload already running server-side. Without this, leaving the
  // page (or reloading) dropped the progress panel while the upload continued, and the item
  // list then showed a partial catalog with nothing saying an upload was in flight.
  React.useEffect(() => {
    let cancelled = false;
    getActiveJob()
      .then((j) => {
        if (!cancelled && j) setActiveJobId(j.jobId);
      })
      .catch(() => {
        /* no active job, or not reachable - nothing to reattach to */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFilePicked = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileInputRef: React.RefObject<HTMLInputElement>,
    uploadButtonRef: React.RefObject<HTMLButtonElement>,
  ) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    const isValidExt = /\.(csv|xlsx)$/i.test(file.name);
    if (!isValidExt) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(null, "Only Excel (.xlsx) or CSV (.csv) files can be uploaded");
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (uploadButtonRef.current) uploadButtonRef.current.blur();
      return;
    }

    setSelectedFile(file);
    setShowUploadConfirm(true);
  };

  const confirmUpload = async (fileInputRef: React.RefObject<HTMLInputElement>) => {
    if (!selectedFile) return;
    setIsUploading(true);
    setShowUploadConfirm(false);

    try {
      const rows = await parseFileToRows(selectedFile);
      if (rows.length === 0) {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(null, "The selected file doesn't contain any rows to upload");
        return;
      }
      if (rows.length > MAX_ROWS_PER_JOB) {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(
          null,
          `This file has too many rows. Maximum is ${MAX_ROWS_PER_JOB.toLocaleString()} per upload`,
        );
        return;
      }

      const chunks = chunkRows(rows, MAX_ROWS_PER_CHUNK);
      const { jobId } = await createUploadJob(rows.length, selectedFile.name);

      // [2026-09-16] UPLOAD_ERROR_VISIBILITY: show the status panel from the moment the job
      // exists, NOT after the last chunk is pushed. A 99,143-row file is 81 chunks and takes
      // minutes to send; setting this afterwards left the user staring at "Uploading..." with
      // no progress for the longest phase of the upload.
      setActiveJobId(jobId);
      setSendProgress({ sent: 0, total: chunks.length });

      // [2026-09-17] UPLOAD_CHUNK_RETRY. One failed chunk used to end the whole upload: on
      // 2026-09-17 chunk 79 of 100 came back 500 from a transient database error, this loop
      // threw, and a 99,143-row file was abandoned at 78% with the job stranded at PENDING.
      // Retry the SAME chunk with backoff; only give up after 4 attempts, and when we do, tell
      // the server so the job ends as FAILED rather than lingering.
      const MAX_ATTEMPTS = 4;
      for (let i = 0; i < chunks.length; i += 1) {
        let lastErr: any = null;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
          try {
            await pushChunk(jobId, i + 1, chunks[i]);
            lastErr = null;
            break;
          } catch (chunkErr: any) {
            const status = chunkErr?.response?.status;
            // 409 means this chunk is already stored - a duplicate, not a failure
            if (status === 409) { lastErr = null; break; }
            // 4xx other than 429 will fail identically on a retry - but the job must still be
            // ended. [2026-09-17] this path used to rethrow straight past abortJob, so a
            // rejected file format on chunk 1 left the job PENDING with chunkCount 0, which the
            // poller never matches (it requires chunkCount > 0) and getActiveJob re-surfaced on
            // every page load as "Queued - waiting to start". Stuck for ever, no way out.
            if (status && status >= 400 && status < 500 && status !== 429) {
              try {
                await abortJob(jobId, `chunk ${i + 1} rejected with ${status}`);
              } catch { /* nothing more we can do from here */ }
              setSendProgress(null);
              setActiveJobId(null);
              throw chunkErr;
            }
            lastErr = chunkErr;
            if (attempt < MAX_ATTEMPTS) {
              await new Promise((r) => setTimeout(r, 500 * 2 ** (attempt - 1))); // 0.5s 1s 2s
            }
          }
        }
        if (lastErr) {
          try {
            await abortJob(jobId, `chunk ${i + 1} of ${chunks.length} failed after ${MAX_ATTEMPTS} attempts`);
          } catch { /* the completeness gate in the uploader catches it either way */ }
          setSendProgress(null);
          setActiveJobId(null);
          throw new Error(
            `The upload stopped at part ${i + 1} of ${chunks.length}. Your existing inventory has not been changed. Please try again.`,
          );
        }
        setSendProgress({ sent: i + 1, total: chunks.length });
      }
      setSendProgress(null);

      try {
        await completeJob(jobId);
      } catch (completeErr: any) {
        if (completeErr?.response?.status !== 409) throw completeErr;
      }

      // [2026-09-15] UPLOAD_ERROR_VISIBILITY: stay on the inventory page and show live
      // status + the failure summary instead of navigating away to the dashboard.
      notify.success(
        "Your file is being processed - you can follow the progress below. We'll also email you the result",
      );
    } catch (err: any) {
      console.error("Upload error:", err);
      // [2026-09-17] a message we raised ourselves already says what happened and that the
      // catalogue is untouched - show it as-is rather than the generic fallback.
      setSendProgress(null);
      setActiveJobId(null);
      notify.error(err, err?.message || "We couldn't complete the upload. Please try again");
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const cancelUpload = (fileInputRef: React.RefObject<HTMLInputElement>) => {
    setSelectedFile(null);
    setShowUploadConfirm(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return {
    isUploading,
    selectedFile,
    showUploadConfirm,
    handleFilePicked,
    confirmUpload,
    cancelUpload,
    // [2026-09-15] UPLOAD_ERROR_VISIBILITY
    activeJobId,
    sendProgress,
    clearActiveJob: () => { setActiveJobId(null); setSendProgress(null); },
    // [2026-09-17] ends a stalled job on the SERVER, not just on screen. Clearing local state
    // alone left the job at PENDING, so it came straight back on the next page load.
    stopActiveJob: async () => {
      if (!activeJobId) return;
      try {
        await abortJob(activeJobId, "stopped by the user from the inventory screen");
        notify.success("Upload stopped. Your inventory has not been changed");
      } catch (e: any) {
        notify.error(e, "We could not stop the upload. Please refresh and try again");
      } finally {
        setActiveJobId(null);
        setSendProgress(null);
      }
    },
  };
};
